<script setup lang="ts">
import type { IValue, IRate } from "~/interfaces";

import FormCard from "~/components/ui/FormCard.vue";
import BackButton from "~/components/inputs/BackButton.vue";
import MaritalStatus from "~/components/inputs/MaritalStatus.vue";
import FormHeader from "~/components/ui/FormHeader.vue";
import Select from "~/components/inputs/Select.vue";
import SelectDate from "~/components/inputs/SelectDate.vue";
import RadioButton from "~/components/inputs/RadioButton.vue";
import InputFloatingLabel from "~/components/inputs/InputFloatingLabel.vue";
import { useFormContainer } from "~/composables/useFormContainer";
import Accordion from "~/components/ui/Accordion.vue";

import Validator from "~/utils/validator";

import { useVuelidate } from "@vuelidate/core";
import personalDataFormRules from "~/themes/cliente-pz/rules/personalDataFormRules";
const PEPLocale = await getLocale("forms", "payment_data");
import { promoAvailable, hasUpgrade, promoDisclaimerMessage } from "~/utils/promo";

defineComponent({
  name: "PersonalDataPz"
})

defineProps({
  progress: { type: Number, required: true },
  title: { type: String, required: true },
  residentSinceStartYear: { type: Number, required: true },
  politicallyExposedOptions: {
    type: Array as () => IValue[],
    default: () => [...yesNoOptions].reverse(),
  },
  stickyButton: { type: Boolean, default: false }
});

const { FormContainer } = useFormContainer();

const emit = defineEmits(["init", "submit","goBack"]);

const maritalStatus = useState<number | null>("maritalStatus", () => null);
const residenceSince = useState<string | null>("residenceSince", () => null);
const housingType = useState<number | null>("housingType", () => null);
const monthlyRent = useState<string | null>("monthlyRent", () => null);
const politicallyExposedPerson = useState<boolean>("politicallyExposedPerson");
const sendingServerRequest = useState("sendingServerRequest", () => false );
const modelKO = ref<IRate | null>(getModelPromoKO());

const showMonthlyRent = computed(() => {
  return housingType.value === 1;
});

watch(showMonthlyRent, val => {
  // reset monthly rent
  if (!val && monthlyRent.value !== null)
    monthlyRent.value = null;
})

const submitText = computed(() => {
  return promoAvailable() && hasUpgrade() ? "CONTINUA" : "INVIA RICHIESTA"
})

const showPromoDisclaimer = computed(() => {
  return false
})

const v$ = useVuelidate(
  personalDataFormRules,
  {
    housingType,
    monthlyRent,
    maritalStatus,
    residenceSince,
    showMonthlyRent,
    politicallyExposedPerson
  }
);

const validate = () => {
  v$.value.$touch();
  v$.value.$validate();
  return !v$.value.$error;
};

const submit = () => {
  if (validate()) {
    emit("submit");
  }
};

onMounted(() => emit("init"));

</script>

<template>
  <FormContainer :progress="progress" class="personal-data-container">
    <FormCard 
      :title="title" @submit="submit" 
      :submit-text="submitText" 
      :has-sticky-button="stickyButton" 
      :has-loading-spinner="true"
      :show-loading-spinner="sendingServerRequest"
    >
      <div class="w-full">
        <MaritalStatus
          v-model="maritalStatus"
          :has-error="v$.maritalStatus.$error"
          :error-message="Validator.message(v$.maritalStatus)"
        />
      </div>
      <div class="w-full">
        <FormHeader>Abitazione</FormHeader>
        <div class="flex flex-wrap">
          <div class="w-full sm:w-1/2 mb-2 mt-3 md:mt-5">
            <Select
              v-model="housingType"
              :values="housingTypeOptions"
              label="Tipologia abitazione"
              disable-zero-value
              floating
              :error="v$.housingType.$error"
              :error-message="Validator.message(v$.housingType)"
              test-id="housingType"
            />
          </div>
          <div v-if="showMonthlyRent" class="w-full sm:w-1/2 sm:pl-2 mb-2 sm:mt-5">
            <InputFloatingLabel
              v-model="monthlyRent"
              label="Affitto mensile"
              type="tel"
              :mask="currencyMask"
              :error="v$.monthlyRent.$error"
              :error-message="Validator.message(v$.monthlyRent)"
              test-id="monthlyRent"
            />
          </div>
        </div>
        <div class="w-full sm:w-1/2 mb-2">
          <label class="text-xs mb-2">Residente dal</label>
          <SelectDate
            v-model="residenceSince"
            :show-day="false"
            :start-year="residentSinceStartYear"
            :error="v$.residenceSince.$error"
            :error-message="Validator.message(v$.residenceSince)"
            test-id="residenceSince"
          />
        </div>
      </div>
      <div class="w-full mt-10 md:mt-5">
        <FormHeader>Persona Politicamente Esposta</FormHeader>
        <div class="w-full flex justify-center">
          <div class="w-full flex md:mt-4 flex-col mb-4">
            <label class="mb-2 text-sm">{{ PEPLocale.politically_exposed }}</label>
            <RadioButton
              v-model="politicallyExposedPerson"
              :values="politicallyExposedOptions"
              :error="v$.politicallyExposedPerson.$error"
              :error-message="Validator.message(v$.politicallyExposedPerson)"
              data-testid="politicallyExposedPerson"
            >
            </RadioButton>
            <small
              v-if="politicallyExposedPerson"
              class="block mt-2 text-red text-[13px]"
            >
              {{ PEPLocale.verify_pep_definition }}
            </small>
            <div class="mt-2">
              <Accordion>
                <div class="text-xs" v-html="PEPLocale.pep_definition"></div>
                <template #items>
                  <div
                    class="py-5 text-gray-600 border-t border-b mt-5"
                    v-html="PEPLocale.pep_explanation"
                  ></div>
                </template>
              </Accordion>
            </div>
          </div>
        </div>
        <div v-if="showPromoDisclaimer && modelKO" class="w-full mt-6">
          <p class="text-sm text-left">
            Cliccando su Invia richiesta prendo atto che il mio profilo non
            soddisfa i
            <button
              type="button"
              class="underline inline"
              @click="updateStateValue('promoModal', true)"
            >
              Termini e condizioni
            </button>
            per richiedere l’offerta promo e che invio richiesta di
            {{ modelKO.imp_finanz_text }} TAEG {{ modelKO.taeg }}%.
          </p>
        </div>
      </div>
      <template #link>
        <BackButton :event="`goBack`" @go-back="$emit('goBack')" />
      </template>
    </FormCard>
  </FormContainer>
</template>
